import { ApolloQueryResult } from '@apollo/client';
import { Text } from '@shared/components';
import { ShipmentQuery } from '@shared/generated/graphql';
import { MovementPreview } from '@shared/types/movement';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { ScheduleStop } from 'clerk_common/types';
import { MdRoute } from 'react-icons/md';
import { VoomaCover } from './VoomaCover/VoomaCover';
import { VoomaSchedule } from './VoomaSchedule/VoomaSchedule';
import { VoomaTrack } from './VoomaTrack';

const ROOT = makeRootClassName('ShipmentCard');
const el = makeElementClassNameFactory(ROOT);

export type RefetchShipmentFn = () => Promise<ApolloQueryResult<ShipmentQuery>>;

type ShipmentCardProps = {
  referenceNumber: string;
  movementPreviews: MovementPreview[];
  shipmentId: string;
  stops: ScheduleStop[];
  trackingInfo: {
    driverName?: string;
    driverPhone?: string;
  };
  tmsId?: string;
  showPauseControl?: boolean;
  refetchShipment: RefetchShipmentFn;
};
export function ShipmentCard({
  referenceNumber,
  shipmentId,
  movementPreviews,
  stops,
  tmsId,
  showPauseControl = false,
  refetchShipment,
}: ShipmentCardProps) {
  if (movementPreviews.length === 0) {
    return <Text>No movements found for this shipment.</Text>;
  }
  if (movementPreviews.length > 1) {
    return <Text>Multiple movements found for this shipment.</Text>;
  }
  return (
    <div className={el`shipment-card`}>
      <ShipmentCardHeader referenceNumber={referenceNumber} tmsId={tmsId} />
      <VoomaSchedule
        shipmentId={shipmentId}
        stops={stops}
        refetchShipment={refetchShipment}
      />
      <VoomaCover shipmentId={shipmentId} movementId={movementPreviews[0].id} />
      <VoomaTrack
        shipmentId={shipmentId}
        movementId={movementPreviews[0].id}
        isPaused={movementPreviews[0].isTrackingPaused}
        showPauseControl={showPauseControl}
        onTrackingPausedChange={refetchShipment}
        stops={stops}
        movementEvents={movementPreviews[0]?.movementEvents ?? []}
      />
    </div>
  );
}

type ShipmentCardHeaderProps = {
  referenceNumber: string;
  tmsId?: string;
};

function ShipmentCardHeader({
  referenceNumber,
  tmsId,
}: ShipmentCardHeaderProps) {
  return (
    <div className={el`shipment-header-refnum`}>
      <ShipmentCardHeaderIcon />
      <div className="flex flex-1 flex-col items-start">
        <Text
          type="body-sm"
          isHeavy
          className="ml-2 line-clamp-1 text-gray-700"
        >
          {tmsId || referenceNumber}
        </Text>
      </div>
    </div>
  );
}

function ShipmentCardHeaderIcon() {
  return (
    <div className="flex items-center justify-center rounded-md bg-gray-100 p-1 text-gray-600">
      <MdRoute size={14} />
    </div>
  );
}
